import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@components/shadcn-ui/button";
import { Input } from "@components/shadcn-ui/input";
import { Label } from "@components/shadcn-ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/shadcn-ui/select";
import { useToast } from "@customHooks/useToast";
import { Loader2 } from "lucide-react";
import { AddressForm } from "./AddressForm";
import { addressService, AddressResponse } from "../service/addressService";

export interface ReferenceFormData {
  relationToApplicant: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  };
  addressId: string;
}

interface ReferenceFormProps {
  applicationId: string;
  leadId: string;
  onReferenceAdded: (reference: ReferenceFormData) => void;
  onCancel: () => void;
  initialData?: ReferenceFormData;
  isEditMode?: boolean;
  existingPhoneNumbers: string[];
  leadPhoneNumber: string;
}

export const ReferenceForm: React.FC<ReferenceFormProps> = ({
  applicationId,
  leadId,
  onReferenceAdded,
  onCancel,
  initialData,
  isEditMode = false,
  existingPhoneNumbers,
  leadPhoneNumber,
}) => {
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<ReferenceFormData>({
    defaultValues: initialData || {
      relationToApplicant: "",
      personalDetails: {
        firstName: "",
        lastName: "",
        phoneNumber: "",
      },
      addressId: "",
    },
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressId, setAddressId] = useState<string>(initialData?.addressId || "");
  const { toast } = useToast();
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressData, setAddressData] = useState<AddressResponse | null>(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  
  // Watch phone number for validation purposes only
  watch("personalDetails.phoneNumber");

  // Load address data when editing a reference
  useEffect(() => {
    const fetchAddressData = async () => {
      if (isEditMode && initialData?.addressId) {
        setIsLoadingAddress(true);
        try {
          const address = await addressService.getAddressById(applicationId, initialData.addressId);
          setAddressData(address);
          setAddressId(address.id);
        } catch (error) {
          console.error("Error fetching address data:", error);
          toast({
            variant: "destructive",
            description: "Failed to load address data",
          });
        } finally {
          setIsLoadingAddress(false);
        }
      }
    };

    fetchAddressData();
  }, [initialData?.addressId, isEditMode, toast, applicationId]);

  const handleAddressAdded = (address: AddressResponse) => {
    setAddressId(address.id);
    setAddressData(address);
    setShowAddressForm(false);
  };

  const validatePhoneNumber = (value: string) => {
    // Check if phone number is the same as lead's phone number
    if (value === leadPhoneNumber) {
      return "Reference phone number cannot be the same as the applicant's phone number";
    }
    
    // Check if phone number is already used in another reference
    if (existingPhoneNumbers.includes(value) && (!initialData || initialData.personalDetails.phoneNumber !== value)) {
      return "This phone number is already used for another reference";
    }
    
    return true;
  };

  const onSubmit = async (data: ReferenceFormData) => {
    if (!addressId) {
      toast({
        variant: "destructive",
        description: "Please add an address for the reference",
      });
      return;
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumber(data.personalDetails.phoneNumber);
    if (phoneValidation !== true) {
      toast({
        variant: "destructive",
        description: phoneValidation,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create the reference object with the address ID
      const referenceData: ReferenceFormData = {
        ...data,
        addressId: addressId,
      };
      
      onReferenceAdded(referenceData);
      toast({
        description: `Reference ${isEditMode ? "updated" : "added"} successfully`,
      });
    } catch (error) {
      console.error("Error saving reference:", error);
      toast({
        variant: "destructive",
        description: `Failed to ${isEditMode ? "update" : "add"} reference`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-2 max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-lg font-medium mb-4">
          {isEditMode ? "Edit Reference" : "Add Reference"}
        </h3>
        
        <form onSubmit={(e) => {
          e.preventDefault(); // Prevent default form submission
          handleSubmit(onSubmit)(e); // Use react-hook-form's handleSubmit
        }} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="relationToApplicant">Relation to Applicant*</Label>
              <Select
                defaultValue={initialData?.relationToApplicant || ""}
                onValueChange={(value) => setValue("relationToApplicant", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Friend">Friend</SelectItem>
                  <SelectItem value="Wife">Wife</SelectItem>
                  <SelectItem value="Brother">Brother</SelectItem>
                  <SelectItem value="Father">Father</SelectItem>
                  <SelectItem value="Colleague">Colleague</SelectItem>
                  <SelectItem value="Relative">Relative</SelectItem>
                  <SelectItem value="Neighbor">Neighbor</SelectItem>
                  <SelectItem value="Family Friend">Family Friend</SelectItem>
                  <SelectItem value="Business Associate">Business Associate</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.relationToApplicant && (
                <p className="text-red-500 text-sm">{errors.relationToApplicant.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="personalDetails.firstName">First Name*</Label>
              <Input
                id="personalDetails.firstName"
                {...register("personalDetails.firstName", { required: "First name is required" })}
              />
              {errors.personalDetails?.firstName && (
                <p className="text-red-500 text-sm">{errors.personalDetails.firstName.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="personalDetails.lastName">Last Name*</Label>
              <Input
                id="personalDetails.lastName"
                {...register("personalDetails.lastName", { required: "Last name is required" })}
              />
              {errors.personalDetails?.lastName && (
                <p className="text-red-500 text-sm">{errors.personalDetails.lastName.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="personalDetails.phoneNumber">Phone Number*</Label>
              <Input
                id="personalDetails.phoneNumber"
                {...register("personalDetails.phoneNumber", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^(?!0)\d{10}$/,
                    message: "Phone number must be valid with 10 digits and cannot start with 0",
                  },
                })}
              />
              {errors.personalDetails?.phoneNumber && (
                <p className="text-red-500 text-sm">{errors.personalDetails.phoneNumber.message}</p>
              )}
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="text-md font-medium mb-2">Reference Address</h4>
            {isLoadingAddress ? (
              <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">Loading address...</span>
              </div>
            ) : addressData && !showAddressForm ? (
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-sm text-gray-500">Address Line 1</p>
                    <p className="font-medium">{addressData.line1}</p>
                  </div>
                  {addressData.line2 && (
                    <div>
                      <p className="text-sm text-gray-500">Address Line 2</p>
                      <p className="font-medium">{addressData.line2}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">City</p>
                    <p className="font-medium">{addressData.city}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">State</p>
                    <p className="font-medium">{addressData.state}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Postal Code</p>
                    <p className="font-medium">{addressData.postalCode}</p>
                  </div>
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowAddressForm(true)}
                >
                  Edit Address
                </Button>
              </div>
            ) : showAddressForm ? (
              <AddressForm
                applicationId={applicationId}
                overrideAddressTypes={["REFERENCE_CURRENT", "REFERENCE_PERMANENT"]}
                leadId={leadId}
                onAddressAdded={handleAddressAdded}
                onCancel={() => {
                  if (addressData) {
                    setShowAddressForm(false);
                  } else {
                    onCancel();
                  }
                }}
                initialData={addressData || undefined}
                isEditMode={!!addressData}
              />
            ) : (
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-600">Please add an address for the reference</p>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowAddressForm(true)}
                >
                  Add Address
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              type="button" 
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditMode ? "Update" : "Add"} Reference
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
